import React from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import GoldShieldLogo from "@/components/GoldShieldLogo";

const CookiePolicy = () => (
  <div className="min-h-screen bg-slate-950 text-white flex flex-col">
    <SEO 
      title="Cookie Policy | RTS EXPRESS LTD"
      description="Cookie policy for RTS EXPRESS LTD website. Learn about the cookies we use and how to manage your preferences."
      path="/cookie-policy"
    />
    <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="legal-nav-home-link">
          <GoldShieldLogo className="w-10 h-12" />
          <div className="flex flex-col">
            <span className="text-xl font-bold text-white tracking-tight leading-tight">RTS EXPRESS</span>
            <span className="text-[10px] text-slate-400 tracking-wide">SAME DAY COURIER</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-8">
          <a href="/#services" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-services-link">Services</a>
          <a href="/#how-it-works" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-how-link">How It Works</a>
          <a href="/#contact" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-nav-contact-link">Contact</a>
        </div>
        <Link to="/get-quote" className="bg-green-500 text-slate-950 hover:bg-green-500/90 font-semibold px-6 py-2 rounded-sm transition-colors" data-testid="legal-get-quote-btn">
          Get a Quote
        </Link>
      </div>
    </nav>

    <main className="flex-1 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="legal-page-title">Cookie Policy</h1>
        <div className="text-slate-300 space-y-4" data-testid="legal-page-content">
          <p><strong>Last updated: 17 February 2026</strong></p>
          <p>
            This Cookie Policy explains how RTS EXPRESS LTD uses cookies and similar technologies on our website.
          </p>

          <h2 className="text-2xl font-semibold text-white">1. What are cookies?</h2>
          <p>
            Cookies are small text files stored on your device when you visit a website. They help the site function, improve user experience, and provide analytics.
          </p>

          <h2 className="text-2xl font-semibold text-white">2. Types of cookies we may use</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>Strictly necessary cookies: required for core site functionality (e.g., security, session management).</li>
            <li>Preference cookies: remember your settings and preferences.</li>
            <li>Analytics/performance cookies: help us understand site usage and improve performance.</li>
            <li>Marketing cookies: used to measure marketing effectiveness (only where applicable and consented).</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white">Analytics Cookies (PostHog)</h2>
          <p>
            We use PostHog, a privacy-focused analytics service, to understand how visitors use our website and to improve performance and user experience.
          </p>
          <p>PostHog may collect information such as:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Pages visited</li>
            <li>Time spent on pages</li>
            <li>Device and browser information</li>
            <li>Interaction behaviour (including session recordings, where enabled)</li>
          </ul>
          <p>
            <strong>Provider:</strong> PostHog<br />
            <strong>Purpose:</strong> Website analytics and performance improvement<br />
            <strong>Legal basis:</strong> Consent<br />
            <strong>Retention period:</strong> As configured in our analytics settings
          </p>
          <p>
            PostHog cookies and tracking technologies are only activated after the user provides explicit consent to non-essential cookies via our cookie banner.
          </p>
          <p>
            If the user rejects non-essential cookies, PostHog must not load and no analytics tracking should occur.
          </p>

          <h2 className="text-2xl font-semibold text-white">3. Cookie Consent</h2>
          <p>
            Where required by law, we request user consent before placing non-essential cookies on the device.
          </p>
          <p>Users can choose to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Accept non-essential cookies</li>
            <li>Reject non-essential cookies</li>
          </ul>
          <p>
            Non-essential cookies (including analytics cookies) must only be set after explicit consent.
          </p>
          <p>
            Users must be able to withdraw or change their consent at any time.
          </p>

          <h2 className="text-2xl font-semibold text-white">4. Managing cookies</h2>
          <p>
            You can manage cookies through your browser settings. Disabling some cookies may affect site functionality.
          </p>

          <h2 className="text-2xl font-semibold text-white">5. Contact</h2>
          <p>
            If you have questions about cookies, contact info@rtsexpress.co.uk.
          </p>
          <p>
            For data protection matters contact: gdpr@rtsexpress.co.uk
          </p>
        </div>
      </div>
    </main>

    <footer className="border-t border-slate-800 bg-slate-950">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm" data-testid="legal-footer-copyright">
            Copyright {new Date().getFullYear()} RTS EXPRESS LTD. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>Company No: 13973121</span>
            <span className="text-slate-700">|</span>
            <span>VAT No: GB414935787</span>
            <span className="text-slate-700">|</span>
            <span>Payment required before collection</span>
          </div>
        </div>
        <p className="mt-2 text-slate-500 text-sm" data-testid="legal-footer-email">
          info@rtsexpress.co.uk
        </p>
        <div className="mt-4 flex flex-wrap gap-4 text-sm">
          <Link to="/terms-and-conditions" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-terms-link">
            Terms &amp; Conditions
          </Link>
          <Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-privacy-link">
            Privacy Policy
          </Link>
          <Link to="/cookie-policy" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-cookie-link">
            Cookie Policy
          </Link>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
            className="text-slate-400 hover:text-white transition-colors"
            data-testid="legal-footer-cookie-settings-button"
          >
            Cookie Settings
          </button>
          <Link to="/website-disclaimer" className="text-slate-400 hover:text-white transition-colors" data-testid="legal-footer-disclaimer-link">
            Website Disclaimer
          </Link>
        </div>
      </div>
    </footer>
  </div>
);

export default CookiePolicy;